import styled from 'styled-components';
import TopicsFilter from './TopicsFilter';
import LawmakerSelect from './LawmakerSelect';
import ChamberFilter from './ChamberFilter';
import KeywordSearch from './KeywordSearch';
import { BillFilter } from './BillFilter';
import TipSheetTypeFilter from './TipSheetTypeFilter';
import FilterChoices from './FilterChoices.jsx';
import { CloseIcon } from '../TipSheetIcons/close-icon.jsx';

// Styled components
const OffcanvasOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 100;
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  transition: opacity 0.3s;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};

  @media (min-width: 769px) {
    display: none; /* Hide on larger screens */
  }
`;

const OffcanvasDrawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 85%;
  max-width: 400px;
  background-color: #ffffff;
  z-index: 101;
  transform: ${(props) =>
    props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  overflow-y: auto;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;

  @media (min-width: 769px) {
    display: none; /* Hide on larger screens */
  }
`;

const OffcanvasHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OffcanvasTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: #0E4C60;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`;

const OffcanvasBody = styled.div`
  padding: 1rem;
  flex: 1;
  overflow-y: auto;
`;

const FilterSection = styled.div`
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 1.5rem;
`;

const SectionLabel = styled.div`
  font-size: 0.9rem;
  color: #000000;
  margin-bottom: 1rem;
`;

const OffcanvasFooter = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  background: #ffffff;
  border-top: 1px solid #e0e0e0;
  padding: 0.75rem;
  display: flex;
  justify-content: center;
  z-index: 102;
`;

const ViewResultsButton = styled.button`
  width: 100%;
  max-width: 420px;
  border: none;
  border-radius: 0.375rem;
  background: #0a819f;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 1rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &:hover,
  &:focus {
    background: #096c83;
  }
`;

export default function OffcanvasFilter({
  isOpen,
  setIsOpen,
  filterState,
  filterHandlers,
  originalStories,
  setFilteredStoriesByBill,
  setBillFilterActive,
  setSelectedBills,
  hideInfo,
  selectedBills,
  tipSheetCount,
}) {
  const {
    allTags,
    geoTags,
    keywords,
    tipsheetType,
    selectedExclusions,
  } = filterState;

  const {
    handleTopicClick,
    handleLawmakerSelect,
    handleExclusionsClick,
    setTipsheetType,
    performSearch,
    setKeywords,
    setSearchActive,
    setSearchStories,
  } = filterHandlers;

  return (
    <>
      <OffcanvasOverlay $isOpen={isOpen} onClick={() => setIsOpen(false)} />

      <OffcanvasDrawer $isOpen={isOpen} className="offcanvas-drawer">
        <OffcanvasHeader>
          <OffcanvasTitle>Filters</OffcanvasTitle>
          <CloseButton onClick={() => setIsOpen(false)}>
            <CloseIcon />
          </CloseButton>
        </OffcanvasHeader>

        <OffcanvasBody>
          <FilterChoices
            variant="mobile"
            tags={filterState.tags}
            setTags={filterHandlers.setTags}
            legislators={filterState.legislators}
            setLegislators={filterHandlers.setLegislators}
            selectedBills={selectedBills}
            setSelectedBills={setSelectedBills}
            keywords={keywords}
            setKeywords={setKeywords}
            setBillFilterActive={setBillFilterActive}
            setFilteredStoriesByBill={setFilteredStoriesByBill}
            setSearchActive={setSearchActive}
            setSearchStories={setSearchStories}
            searchInputRef={null}
            originalStories={originalStories}
            coverageActive={false}
            chamberFilter={filterState.chamberFilter}
            setChamberFilter={filterHandlers.setChamberFilter}
          />

          <FilterSection>
            <SectionLabel>Topics</SectionLabel>
            <TopicsFilter
              allTags={allTags}
              geoTags={geoTags}
              handleTopicClick={handleTopicClick}
              isMobile={true}
            />
          </FilterSection>

          <FilterSection>
            <SectionLabel>Legislators</SectionLabel>
            <LawmakerSelect
              lawmakers={filterState.lawmakerOptions}
              handleLawmakerSelect={handleLawmakerSelect}
              isMobile={true}
              chamberFilter={filterState.chamberFilter}
              setChamberFilter={filterHandlers.setChamberFilter}
            />
          </FilterSection>

          <FilterSection>
            <SectionLabel>Bills</SectionLabel>
            <BillFilter
              originalStories={originalStories}
              setFilteredStoriesByBill={setFilteredStoriesByBill}
              setFilterActive={setBillFilterActive}
              setSelectedBills={setSelectedBills}
              selectedBills={selectedBills}
              isMobile={true}
            />
          </FilterSection>

          <FilterSection>
            <SectionLabel>Keywords</SectionLabel>
            <KeywordSearch
              keywords={keywords}
              setKeywords={setKeywords}
              performSearch={performSearch}
              setSearchActive={setSearchActive}
              setSearchStories={setSearchStories}
              isMobile={true}
            />
          </FilterSection>
          <FilterSection>
            <ChamberFilter
              chamberFilter={filterState.chamberFilter}
              setChamberFilter={filterHandlers.setChamberFilter}
              name="chamberFilterOffcanvas"
            />
          </FilterSection>
          <FilterSection>
            <TipSheetTypeFilter
              tipsheetType={tipsheetType}
              setTipsheetType={setTipsheetType}
              selectedExclusions={selectedExclusions}
              handleExclusionsClick={handleExclusionsClick}
              hideInfo={hideInfo}
              isMobile={true}
              variant="sidebar"
            />
          </FilterSection>

        </OffcanvasBody>
        <OffcanvasFooter>
          <ViewResultsButton
            type="button"
            onClick={() => setIsOpen(false)}
          >
            View Results ({tipSheetCount ?? 0})
          </ViewResultsButton>
        </OffcanvasFooter>
      </OffcanvasDrawer>
    </>
  );
}
